import { Breadcrumb, Divider, Layout, theme, Row, Col, Spin, Alert, Select, DatePicker, Button } from "antd";
import { Content } from "antd/es/layout/layout";
import { CalendarOutlined } from "@ant-design/icons";
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import SalesCard from './components/SalesCard/SalesCard';
import SalesComparisonChart from './components/SalesComparisonChart/SalesComparisonChart';
import ProductMixChart from './components/ProductMixChart/ProductMixChart';
import EvolutionTable from './components/EvolutionTable/EvolutionTable';
import AccountsSearchSelect from '../../common/AccountsSearchSelect';
import { apiSalesByDate, apiReceivablesByDate, apiProductByDate, apiAccountSalesComparison } from '../../../api/salesMetrics';
import { format, subDays, startOfMonth, subMonths } from 'date-fns';
import './Home.scss';
import dayjs from "dayjs";

const Home = () => {
  const navigate = useNavigate();
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();  const [todaySales, setTodaySales] = useState(0);
  const [todayReceivables, setTodayReceivables] = useState(0);
  const [currentWeekSales, setCurrentWeekSales] = useState([]);
  const [previousWeekSales, setPreviousWeekSales] = useState([]);
  const [todayProductMix, setTodayProductMix] = useState([]);
  const [evolutionData, setEvolutionData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [evolutionLoading, setEvolutionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewType, setViewType] = useState('daily'); // 'daily' or 'monthly'
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [customDateRange, setCustomDateRange] = useState([dayjs(subDays(new Date(), 7)), dayjs(new Date())]); // For custom date range

  useEffect(() => {
    if (viewType === 'daily') {
      const today = new Date();
      let startDate, endDate;
      endDate = today;
      startDate = subDays(today, 7);
      setCustomDateRange([dayjs(startDate), dayjs(endDate)]);
    } else if (viewType === 'monthly') {
      const today = new Date();
      let startDate, endDate;
      endDate = today;
      startDate = startOfMonth(today);
      setCustomDateRange([dayjs(startDate), dayjs(endDate)]);
    }
  }, [viewType]);
  
  const fetchTodaySales = useCallback(async () => {
    try {
      setError(null);
      
      const filters = {}
      if (viewType === 'daily') {
        filters.date_after = format(customDateRange[1].toDate(), 'yyyy-MM-dd');
      } else {
        filters.date_after = format(customDateRange[0].toDate(), 'yyyy-MM-dd');
        filters.date_before = format(customDateRange[1].toDate(), 'yyyy-MM-dd');
      }
      
      const response = await apiSalesByDate(filters, selectedAccount?.id);
      const salesData = response.results || response || [];
      
      // Sum all the total values from the API response
      const totalSales = salesData.reduce((sum, item) => {
        return sum + parseFloat(item.total || 0);
      }, 0);
      
      setTodaySales(totalSales / 100);
    } catch (err) {
      console.error('Error fetching sales by date:', err);
      setError('Erro ao carregar dados de vendas');
    }
  }, [customDateRange, selectedAccount]);  // Fetch today's receivables from the new API

  const fetchTodayReceivables = useCallback(async () => {
    try {
      setError(null);
      
      let filters = {};
      if (viewType === 'daily') {
        filters.date_after = format(customDateRange[1].toDate(), 'yyyy-MM-dd');
      } else {
        filters.date_after = format(customDateRange[0].toDate(), 'yyyy-MM-dd');
        filters.date_before = format(customDateRange[1].toDate(), 'yyyy-MM-dd');
      }

      const response = await apiReceivablesByDate(filters, selectedAccount?.id);
      const receivablesData = response.results || response || [];
      
      // Sum all the total values from the API response
      const totalReceivables = receivablesData.reduce((sum, item) => {
        return sum + parseFloat(item.total || 0);
      }, 0);
      
      setTodayReceivables(totalReceivables / 100);
    } catch (err) {
      console.error('Error fetching receivables by date:', err);
      setError('Erro ao carregar dados de recebíveis');
    }
  }, [customDateRange, selectedAccount]);  // Fetch current week sales (last 7 days)

  const fetchCurrentWeekSales = useCallback(async () => {
    try {
      setError(null);
      const filters = {}
      filters.date_after = format(customDateRange[0].toDate(), 'yyyy-MM-dd');
      filters.date_before = format(customDateRange[1].toDate(), 'yyyy-MM-dd');
      
      const response = await apiSalesByDate(filters, selectedAccount?.id);
      const salesData = response.results || response || [];
      salesData.reverse(); // Reverse to show latest first
      setCurrentWeekSales(salesData);
    } catch (err) {
      console.error('Error fetching current week sales:', err);
      setError('Erro ao carregar dados de vendas da semana atual');
    }
  }, [customDateRange, selectedAccount]);  // Fetch previous week sales (7-14 days ago)

  const fetchPreviousWeekSales = useCallback(async () => {
    try {
      setError(null);
      
      let today = new Date();
      let startDate, endDate;
      if (viewType === 'monthly') {
        // For monthly view: same period as current month, but from last month
        const lastMonth = subMonths(today, 1);
        const lastMonthStart = startOfMonth(lastMonth);
        
        // Calculate the day of the month for today
        const currentDay = today.getDate();
        const lastMonthSameDay = new Date(lastMonth.getFullYear(), lastMonth.getMonth(), currentDay);
        
        startDate = lastMonthStart;
        endDate = lastMonthSameDay;
      } else {
        startDate = customDateRange[0].toDate();
        endDate = customDateRange[1].toDate(); 
        // Calculate the previous period with the same duration
        const diff = endDate.getTime() - startDate.getTime();
        endDate = new Date(startDate.getTime() - 1); // Previous period ends 1ms before current period starts
        startDate = new Date(endDate.getTime() - diff);
      }
      
      const filters = {
        date_before: format(endDate, 'yyyy-MM-dd'),
        date_after: format(startDate, 'yyyy-MM-dd')
      };
      
      const response = await apiSalesByDate(filters, selectedAccount?.id);
      const salesData = response.results || response || [];
      salesData.reverse(); // Reverse to show latest first
      setPreviousWeekSales(salesData);
    } catch (err) {
      console.error('Error fetching previous week sales:', err);
      setError('Erro ao carregar dados de vendas da semana anterior');
    }
  }, [customDateRange, selectedAccount]);  // Fetch today's product mix

  const fetchTodayProductMix = useCallback(async () => {
    try {
      setError(null);
      
      let filters = {};
      if (viewType === 'daily') {
        filters.date_after = format(customDateRange[1].toDate(), 'yyyy-MM-dd');
      } else {
        filters.date_after = format(customDateRange[0].toDate(), 'yyyy-MM-dd');
        filters.date_before = format(customDateRange[1].toDate(), 'yyyy-MM-dd');
      }
      
      const response = await apiProductByDate(filters, selectedAccount?.id);
      const productData = response.results || response || [];
      
      setTodayProductMix(productData);
    } catch (err) {
      console.error('Error fetching product mix:', err);
      setError('Erro ao carregar dados de mix de produtos');
    }
  }, [customDateRange, selectedAccount]);

  const fetchEvolutionData = useCallback(async () => {
    try {
      setEvolutionLoading(true);
      setError(null);
      
      const filters = {
        date_before: format(customDateRange[0].toDate(), 'yyyy-MM-dd'),
        date_after: format(customDateRange[1].toDate(), 'yyyy-MM-dd')
      };
      
      const response = await apiAccountSalesComparison(filters, selectedAccount?.id);
      const evolutionDataResponse = response.results || response || [];
      
      setEvolutionData(evolutionDataResponse);
    } catch (err) {
      console.error('Error fetching evolution data:', err);
      setError('Erro ao carregar dados de evolução');
    } finally {
      setEvolutionLoading(false);
    }
  }, [customDateRange]);
  
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([
        fetchTodaySales(),
        fetchTodayReceivables(),
        fetchCurrentWeekSales(),
        fetchPreviousWeekSales(),
        fetchTodayProductMix()
      ]);
      setLoading(false);
      
      // Fetch evolution data separately to avoid blocking main dashboard
      fetchEvolutionData();
    };
    
    fetchData();
  }, [fetchTodaySales, fetchTodayReceivables, fetchCurrentWeekSales, fetchPreviousWeekSales, fetchTodayProductMix, fetchEvolutionData]);

  return (
    <div className='Home'>
      <Layout
        style={{
          padding: "1.25rem",
        }}
      >
        <Breadcrumb
          style={{
            margin: "16px 0",
          }}
        >          <div style={{ display: "flex", width: "100%", justifyContent: "space-between", alignItems: "center" }}>
            <h2 className="title-page">Dashboard</h2>
            <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
              <Button
                type="primary"
                icon={<CalendarOutlined />}
                onClick={() => navigate('/dashboard/receivable')}
                style={{ fontWeight: 600 }}
              >
                Resultado Recebíveis
              </Button>
              <AccountsSearchSelect
                style={{ width: 200 }}
                placeholder="Filtrar por conta"
                value={selectedAccount}
                onChange={setSelectedAccount}
                allowClear={true}
              />
              <Select
                value={viewType}
                onChange={setViewType}
                style={{ width: 140 }}
                options={[
                  { value: 'daily', label: 'Visão Diária' },
                  { value: 'monthly', label: 'Visão Mensal' },
                  { value: 'custom', label: 'Personalizado' }
                ]}
              />
              {viewType === 'custom' && (
                <DatePicker.RangePicker
                  value={customDateRange}
                  onChange={setCustomDateRange}
                  format={{ format: 'DD/MM/YYYY', type: 'mask' }}
                  style={{ width: 280 }}
                  placeholder={['Data inicial', 'Data final']}
                />
              )}
            </div>
          </div>
          <Divider />
          <Breadcrumb.Item>Dashboard</Breadcrumb.Item>
        </Breadcrumb>
        <Content
          style={{
            padding: 24,
            margin: 0,
            minHeight: 280,
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
          }}
        >
          {error && (
            <Alert
              message="Erro ao carregar dados"
              description={error}
              type="error"
              showIcon
              style={{ marginBottom: 24 }}
            />
          )}
            <Spin spinning={loading} tip="Carregando dados...">            <div className="dashboard-container">              {/* Sales Metrics - Today's Sales */}
              <div className="dashboard-section">
                <Row gutter={[24, 24]} className="sales-row">
                  <Col xs={24} sm={12} lg={12}>
                    <SalesCard
                      title={viewType === 'daily' ? "Vendas no dia" : "Vendas do mês atual"}
                      value={todaySales || 0}
                      prefix="R$"
                      trend={null}
                      loading={loading}
                    />
                  </Col>
                  <Col xs={24} sm={12} lg={12}>
                    <SalesCard
                      title={viewType === 'daily' ? "Vendas Líquidas no dia" : "Vendas Líquidas do mês atual"}
                      value={todayReceivables || 0}
                      prefix="R$"
                      trend={null}
                      loading={loading}
                    />
                  </Col>
                </Row>
              </div>{/* Sales Comparison Chart */}
              <div className="dashboard-section" style={{ marginTop: '24px' }}>
                <Row gutter={[24, 24]}>                  <Col xs={24} lg={16}>
                    <SalesComparisonChart
                      title={viewType === 'daily' 
                        ? "Vendas autorizadas - Comparativo semana a semana"
                        : "Vendas autorizadas - Comparativo mês atual vs mês anterior"
                      }
                      currentWeekData={currentWeekSales}
                      previousWeekData={previousWeekSales}
                      loading={loading}
                    />
                  </Col>                  <Col xs={24} lg={8}>
                    <ProductMixChart
                      title={viewType === 'daily' ? "Mix de Produtos" : "Mix de Produtos - Mês atual"}
                      productData={todayProductMix}
                      loading={loading}
                    />
                  </Col>
                </Row>
              </div>

              {/* Evolution Table */}
              <div className="dashboard-section" style={{ marginTop: '24px' }}>
                <Row gutter={[24, 24]}>
                  <Col xs={24}>
                    <EvolutionTable
                      data={evolutionData}
                      loading={evolutionLoading}
                    />
                  </Col>
                </Row>
              </div>
            </div>
          </Spin>
        </Content>
      </Layout>
    </div>
  )
}

export default Home
